import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Alert,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  InputAdornment,
  Avatar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import api from '../services/api';

interface User {
  id: string;
  email: string;
  displayName: string;
  isActive: boolean;
  systemRole: string;
  lastLoginAt: string;
  createdAt: string;
}

interface AssignedUser {
  id: string;
  email: string;
  displayName: string;
  role: string;
  grantedBy: string;
  grantedAt: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface UserManagementDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  entityId: string;
  entityType: 'company' | 'account';
  organizationId: string;
}

const ROLE_OPTIONS = [
  { value: 'OWNER', label: 'Owner', color: 'error' },
  { value: 'MANAGER', label: 'Manager', color: 'warning' },
  { value: 'MEMBER', label: 'Member', color: 'primary' }
];

export default function UserManagementDialog({
  open,
  onClose,
  title,
  entityId,
  entityType,
  organizationId
}: UserManagementDialogProps) {
  const [assignedUsers, setAssignedUsers] = useState<AssignedUser[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [selectedRole, setSelectedRole] = useState<string>('MEMBER');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (open && entityId) {
      loadAssignedUsers();
      loadAvailableUsers();
    }
  }, [open, entityId, organizationId]);

  const loadAssignedUsers = async () => {
    try {
      setLoading(true);
      const response = entityType === 'company' 
        ? await api.getCompanyUsers(entityId)
        : await api.getAccountUsers(entityId);
      setAssignedUsers(response.data || []);
    } catch (err: any) {
      console.error('Error loading assigned users:', err);
      setError(err.response?.data?.message || 'Failed to load assigned users');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableUsers = async () => {
    try {
      const response = await api.getOrganizationUsers(organizationId);
      const allUsers = response.data || [];
      
      // Filter out users who are already assigned
      const assignedUserIds = assignedUsers.map(user => user.id);
      const available = allUsers.filter((user: User) => !assignedUserIds.includes(user.id));
      setAvailableUsers(available);
    } catch (err: any) {
      console.error('Error loading available users:', err);
    }
  };

  const handleAssignUser = async () => {
    if (!selectedUser || !selectedRole) return;

    try {
      setLoading(true);
      if (entityType === 'company') {
        await api.assignUserToCompany(entityId, {
          userId: selectedUser,
          role: selectedRole
        });
      } else {
        await api.assignUserToAccount(entityId, {
          userId: selectedUser,
          role: selectedRole
        });
      }
      
      setSuccess('User assigned successfully');
      setAssignDialogOpen(false);
      setSelectedUser('');
      setSelectedRole('MEMBER');
      loadAssignedUsers();
      loadAvailableUsers();
    } catch (err: any) {
      console.error('Error assigning user:', err);
      setError(err.response?.data?.message || 'Failed to assign user');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = async (userId: string, newRole: string) => {
    try {
      setLoading(true);
      if (entityType === 'company') {
        await api.updateUserCompanyRoleViaCompany(entityId, userId, newRole);
      } else {
        await api.updateUserAccountRoleViaAccount(entityId, userId, newRole);
      }
      
      setSuccess('User role updated successfully');
      loadAssignedUsers();
    } catch (err: any) {
      console.error('Error updating user role:', err);
      setError(err.response?.data?.message || 'Failed to update user role');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to remove this user?')) return;

    try {
      setLoading(true);
      if (entityType === 'company') {
        await api.removeUserFromCompany(entityId, userId);
      } else {
        await api.removeUserFromAccount(entityId, userId);
      }
      
      setSuccess('User removed successfully');
      loadAssignedUsers();
      loadAvailableUsers();
    } catch (err: any) {
      console.error('Error removing user:', err);
      setError(err.response?.data?.message || 'Failed to remove user');
    } finally {
      setLoading(false);
    }
  };

  const getRoleColor = (role: string) => {
    const roleOption = ROLE_OPTIONS.find(r => r.value === role);
    return roleOption?.color || 'default';
  };

  const getRoleLabel = (role: string) => {
    const roleOption = ROLE_OPTIONS.find(r => r.value === role);
    return roleOption?.label || role;
  };

  const filteredAvailableUsers = availableUsers.filter(user =>
    user.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const assignedUsersColumns: GridColDef[] = [
    {
      field: 'displayName',
      headerName: 'Name',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem' }}>
            {params.value?.charAt(0) || params.row.email.charAt(0)}
          </Avatar>
          <Typography variant="body2" noWrap>
            {params.value}
          </Typography>
        </Box>
      )
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary" noWrap>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={getRoleLabel(params.value)}
          color={getRoleColor(params.value) as any}
          size="small"
        />
      )
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 80,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'error'}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Change Role"
          onClick={() => {
            const newRole = prompt('Enter new role (OWNER, MANAGER, MEMBER):', params.row.role);
            if (newRole && ['OWNER', 'MANAGER', 'MEMBER'].includes(newRole)) {
              handleUpdateRole(params.row.id, newRole);
            }
          }}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Remove"
          onClick={() => handleRemoveUser(params.row.id)}
        />
      ]
    }
  ];

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon />
              <Typography variant="h6">{title} - Members ({assignedUsers.length})</Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAssignDialogOpen(true)}
              disabled={loading}
              size="small"
            >
              Add Member
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ height: '70vh', p: 0 }}>
          {assignedUsers.length > 0 ? (
            <DataGrid
              rows={assignedUsers}
              columns={assignedUsersColumns}
              loading={loading}
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 25 }
                }
              }}
              disableRowSelectionOnClick
              sx={{
                border: 'none',
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid #f0f0f0'
                }
              }}
            />
          ) : (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              border: '2px dashed #ccc',
              borderRadius: 1,
              m: 2
            }}>
              <PersonIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body1" color="text.secondary">
                No members assigned yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Click "Add Member" to assign users to this {entityType}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Assign User Dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign User to {title}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              fullWidth
              label="Search users"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
              }}
            />

            <FormControl fullWidth>
              <InputLabel>Select User</InputLabel>
              <Select
                value={selectedUser}
                label="Select User"
                onChange={(e) => setSelectedUser(e.target.value)}
              >
                {filteredAvailableUsers.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    <Box>
                      <Typography variant="body1">{user.displayName}</Typography>
                      <Typography variant="body2" color="text.secondary">{user.email}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={selectedRole}
                label="Role"
                onChange={(e) => setSelectedRole(e.target.value)}
              >
                {ROLE_OPTIONS.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    <Chip
                      label={role.label}
                      color={role.color as any}
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    {role.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleAssignUser} 
            variant="contained"
            disabled={!selectedUser || !selectedRole || loading}
          >
            Assign User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbars */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert onClose={() => setSuccess(null)} severity="success">
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </>
  );
}
